import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { usePaymentHistory } from "@/hooks/usePaymentHistory";
import PaymentTabs from "@/components/payment-history/PaymentTabs";
import ViewTransactionModal from "@/components/payment-history/ViewTransactionModal";
import PaymentFilter from "@/components/payment-history/PaymentFilter";
import PaymentHistoryTable from "@/components/payment-history/PaymentHistoryTable";
import { createPaymentTableColumns } from "@/components/payment-history/PaymentTableColumns";
import ExportConfirmationModal from "@/components/payment-history/ExportConfirmationModal";
import { LightText2 } from "@/common/ui/Text";
import { Button } from "@/common/ui/Button";
import { X } from "lucide-react";
import { PaymentTransaction } from "@/types/payment";
import { AppDispatch } from "@/store/store";
import { setSelectedTransaction, setFilter, setFetchFilter } from "@/store/features/paymentSlice";
import Icon from "@/common/ui/Image";
import useClickOutside from "@/hooks/useClickOutSide";
import { downloadPaymentInvoice } from "@/services/payment.services";
import { FaCaretDown } from "react-icons/fa6";
import { toastMessage } from "@/utils/helper/myToast";
import { loginSelector } from "@/store/features/loginSlice";
import { PERMISSIONS } from "@/utils/constant/global.constant";
import { isHasPermission } from "@/utils/helper/permissions";
import { PermissionDenied } from "@/common/ui/page/PermissionDeniedPage";

const PaymentHistoryPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { currentUser } = useSelector(loginSelector);
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportModalConfig, setExportModalConfig] = useState<{
    exportType: "selected" | "all";
    selectedCount: number;
    transactionIds: string[];
  } | null>(null);
  
  // Permission checks
  const canViewPaymentHistory = isHasPermission(currentUser?.perms, PERMISSIONS.PAYMENT["View Payment History"]);
  const canExportPayments = isHasPermission(currentUser?.perms, PERMISSIONS.PAYMENT["Export Payments"]);
  const canDownloadReceipt = isHasPermission(currentUser?.perms, PERMISSIONS.PAYMENT["Download Receipt"]);
  const canViewIncomingPayments = isHasPermission(currentUser?.perms, PERMISSIONS.PAYMENT["Incomming Payments"]);
  // const canViewOutgoingPayments = isHasPermission(currentUser?.perms, PERMISSIONS.PAYMENT["Outgoing Payments"]);

  if(!canViewIncomingPayments || !canViewPaymentHistory) {
    return <PermissionDenied />;
  }
  const {
    selectedTransaction,
    paymentsData,
    isLoading,
    error,
    refreshData,
    totalCount,
    setPagination,
    pagination,
    applyFilters,
    filters: paymentStateFilters,
    sortBy,
    setSortBy,
  } = usePaymentHistory(!canViewIncomingPayments);
  const [rowSelection, setRowSelection] = useState({});
  const [isSelectAllActive, setIsSelectAllActive] = useState(false);
  const [showActionsOptions, setShowActionsOptions] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<"incoming" | "outgoing">("incoming");
  const actionsRef = useClickOutside(() => setShowActionsOptions(false));
  const paymentFilterRef = useRef<any>(null);

  // Use Redux data from API, fallback to empty array if no data
  const displayData = paymentsData && paymentsData.length > 0 ? paymentsData : [];

  const handleTabChange = (tab: "incoming" | "outgoing") => {
    setSelectedTab(tab);
  };

  const handleViewTransaction = (transaction: PaymentTransaction) => {
    dispatch(setSelectedTransaction(transaction));
    setIsTransactionModalOpen(true);
  };

  const handleCloseTransactionModal = () => {
    setIsTransactionModalOpen(false);
    dispatch(setSelectedTransaction(null));
  };

  const handleDownloadReceipt = async (transactionId: string) => {
    // Check permission first
    if (!canDownloadReceipt) {
      toastMessage("You don't have permission to download receipts.", true);
      return;
    }
    
    if (isDownloading) return; 
    
    setIsDownloading(true);
    
    // Open a new window immediately to avoid Safari popup blocking
    const newWindow = window.open('', '_blank');
    
    try {
      // Call the API to get the invoice URL
      const response = await downloadPaymentInvoice(transactionId);
      
      if (response?.invoiceUrl) {
        // Update the window location with the invoice URL
        if (newWindow) {
          newWindow.location.href = response.invoiceUrl;
        }
      } else {
        throw new Error("No invoice URL received from server");
      }
    } catch (error) {
      console.error("Failed to get invoice URL:", error);
      // Close the window if there was an error
      if (newWindow) {
        newWindow.close();
      }
      alert("Failed to get invoice. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFilterClick = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const handleCloseFilter = () => {
    setIsFilterOpen(false);
  };

  const handleApplyFilters = (filters: any[]) => {
    applyFilters(filters);
  };

  const handleResetFilters = () => {
    dispatch(setFilter([]));
    dispatch(setFetchFilter([]));
    refreshData();
  };

  const handleAddFilter = () => {
    if (paymentFilterRef.current && paymentFilterRef.current.addFilter) {
      paymentFilterRef.current.addFilter();
    }
  };

  const hasEmptyFilter = paymentStateFilters?.some((filter: any) => !filter.field?.value) || false;

  useEffect(() => {
  }, [paymentStateFilters]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      dispatch(setFilter([]));
      dispatch(setFetchFilter([]));
      setSortBy([]);
      
      const resetPagination = {
        pageNumber: 1,
        pageSize: pagination.pageSize,
        page: 1,
        per_page: pagination.pageSize,
        total: totalCount || 0,
        total_pages: Math.ceil((totalCount || 0) / pagination.pageSize),
      };
      setPagination(resetPagination);
      
      setRowSelection({});
      setIsSelectAllActive(false);
      
      await refreshData();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    }
  };


  const handleExportAll = () => {
    if (!canExportPayments) {
      toastMessage("You don't have permission to export payments.", true);
      return;
    }
    
    if (!totalCount || totalCount === 0) {
      toastMessage("There are no records available to export at the moment.", true);
      return;
    }
    
    setExportModalConfig({
      exportType: 'all',
      selectedCount: totalCount || 0,
      transactionIds: []
    });
    setIsExportModalOpen(true);
  };

  const handleExportHistory = () => {
    navigate('/home/Payments-history/export-history');
  };

  const handleBulkAction = () => {
    if (!canExportPayments) {
      toastMessage("You don't have permission to export payments.", true);
      return;
    }
    
    const selectedRowIds = Object.keys(rowSelection);
    const transactionIds = selectedRowIds.map(rowId => {
      const rowIndex = parseInt(rowId);
      return displayData[rowIndex]?.transaction_id || '';
    }).filter(id => id !== '');
    
    const exportCount = isSelectAllActive ? totalCount || 0 : selectedRowIds.length;
    if (!exportCount || exportCount === 0) {
      toastMessage("There are no records available to export at the moment.", true);
      return;
    }
    
    setExportModalConfig({
      exportType: 'selected',
      selectedCount: exportCount,
      transactionIds
    });
    setIsExportModalOpen(true);
  };

  const handleCloseExportModal = () => {
    setIsExportModalOpen(false);
    setExportModalConfig(null);
  };

  const handleExportSuccess = () => {
    setRowSelection({});
    setIsSelectAllActive(false);
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setRowSelection({});
    
    const newPagination = {
      pageNumber: page,
      pageSize: pagination.pageSize,
      page: page,
      per_page: pagination.pageSize,
      total: totalCount || 0,
      total_pages: Math.ceil((totalCount || 0) / pagination.pageSize),
    };
    
    setPagination(newPagination);
    
    setTimeout(() => {
      refreshData();
    }, 100);
  };

  const handlePerPageChange = (perPage: number) => {
    setRowSelection({});
    setIsSelectAllActive(false);
    
    // Update pagination state
    const newPagination = {
      pageNumber: 1,
      pageSize: perPage,
      page: 1,
      per_page: perPage,
      total: totalCount || 0,
      total_pages: Math.ceil((totalCount || 0) / perPage),
    };
    
    setPagination(newPagination);
        setTimeout(() => {
      refreshData();
    }, 100);
  };

  // Sort handler
  const handleSortChange = (newSortBy: Array<{ field: string; isASC: boolean }>) => {
    setRowSelection({});
    setIsSelectAllActive(false);
    
    setSortBy(newSortBy);
    
    setTimeout(() => {
      refreshData();
    }, 100);
  };

  // Dropdown options for table actions
  const dropdownOptions = [
    {
      label: "View",
      onClickFun: (data: PaymentTransaction) => handleViewTransaction(data),
      show: (row: PaymentTransaction) => row.status === 'Paid',
    },
  ];

  const paymentTableColumns = createPaymentTableColumns(handleViewTransaction);


  return (
      <div className="w-full h-full ">

        <div className="px-3 sm:px-4 lg:px-[20px]">
        {/* Header Section */}
        <div className="h-[60px] sm:h-[44px] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-0">
          {Object.keys(rowSelection).length > 0 || isSelectAllActive ? (
            <div className="flex items-center gap-2 h-full">
              {canExportPayments && canViewIncomingPayments && (
                <Button variant="primary" size="small" onClick={handleBulkAction}>
                  Export
                </Button>
              )}
              <Button variant="outline" size="small" className="" onClick={() => {
                setRowSelection({});
                setIsSelectAllActive(false);
              }}>
                Cancel
              </Button>
              <LightText2 text={`${isSelectAllActive ? totalCount : Object.keys(rowSelection).length} ${isSelectAllActive ? totalCount === 1 ? 'payment' : 'payments' : Object.keys(rowSelection).length === 1 ? 'payment' : 'payments'} selected`} />
            </div>
          ) : (
            <>
              <div className="flex items-center h-full">
                <p className="text-sm sm:text-[16px] font-[700] text-[#495057] mr-2">Payments History ({totalCount || 0})</p>
                <Button 
                  variant="transparent" 
                  size="small" 
                  className="!p-1 !min-w-0" 
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                >
                    <Icon 
                      src="/icons/refresh.svg" 
                      alt="refresh icon" 
                      className={`w-6 h-6 ${isRefreshing ? 'animate-spin' : ''}`} 
                    />
                </Button>
              </div>

              <div className="flex items-center gap-2 sm:gap-3 h-full">
            
                {canViewIncomingPayments && (
                  <div className={`p-2 sm:p-3 rounded-[5px] cursor-pointer transition-colors relative bg-[#f8f5fb]`} onClick={handleFilterClick}>
                  <Icon 
                    src="/icons/filter.svg" 
                    alt="filter icon" 
                    className={`w-4 h-4 `} 
                  />
                  {paymentStateFilters?.length > 0 && (
                    <>
                      <div className="absolute -top-1 -right-1 min-w-4 h-4 bg-[#7139b8] rounded-full flex items-center justify-center">
                        <span className="text-white text-10 font-semibold px-1">{paymentStateFilters.length}</span>
                      </div>
                      <div className="absolute top-0 right-0 w-2 h-2 rounded-full bg-red-500"></div>
                    </>
                  )}
                  </div>
                )}
                
                {canExportPayments && canViewIncomingPayments && (
                  <div className={`relative flex rounded-lg gap-[1px] ${
                    showActionsOptions 
                      ? '' 
                      : 'shadow-sm'
                  }`} ref={actionsRef} style={showActionsOptions ? { 
                    boxShadow: '0px 0px 0px 3px rgba(225,214,240,0.75)',
                    WebkitBoxShadow: '0px 0px 0px 3px rgba(225,214,240,0.75)',
                    MozBoxShadow: '0px 0px 0px 3px rgba(225,214,240,0.75)'
                  } : {}}>
                    <button
                      className={`h-[36px] sm:h-[40px] px-2 sm:px-4 py-1 sm:py-2 text-12 sm:text-13 !leading-normal font-medium whitespace-nowrap rounded-l-[4px] active:ring active:ring-brand-shade active:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 border-2 ${
                        showActionsOptions 
                          ? 'bg-[#57219c] border-[#7139b8]' 
                          : 'bg-[#f8f5fb] border-transparent'
                      }`}
                      onClick={() => setShowActionsOptions((prev) => !prev)}
                    >
                      <span className={`text-[13px] font-[400] ${
                        showActionsOptions 
                          ? 'text-white' 
                          : 'text-[#7139b8]'
                      }`}>Actions</span>
                    </button>
                    <button
                      className={`h-[36px] sm:h-[40px] px-1 sm:px-2 text-12 sm:text-13 !leading-normal rounded-r-[4px] border-2 ${
                        showActionsOptions 
                          ? 'bg-[#57219c] border-[#7139b8]' 
                          : 'bg-[#f8f5fb] border-transparent'
                      }`}
                      onClick={() => setShowActionsOptions((prev) => !prev)}
                    >
                      <FaCaretDown  size={14} className={`sm:w-4 sm:h-4 ${
                        showActionsOptions 
                          ? 'text-white' 
                          : 'text-[#7139b8]'
                      }`} />
                    </button>

                    {showActionsOptions && (
                      <div className="absolute top-full right-0 mt-1 w-32 sm:w-40 bg-white border border-gray-200 shadow-xl rounded-md z-[100]">
                        <button
                          className="px-3 sm:px-4 w-full text-start py-2 hover:bg-gray-100 cursor-pointer text-12 sm:text-13 !leading-normal text-gray-700 transition-colors duration-150 rounded-md"
                          onClick={() => {
                            setShowActionsOptions(false);
                            handleExportAll();
                          }}
                        >
                          Export All
                        </button>
                        <button
                          className="px-3 sm:px-4 w-full text-start py-2 hover:bg-gray-100 cursor-pointer text-12 sm:text-13 !leading-normal text-gray-700 transition-colors duration-150 rounded-md"
                          onClick={() => {
                            handleExportHistory();
                          }}
                        >
                          Export History
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>


        {/* Tabs */}
        <div className="mt-6 mb-2 sm:mt-0">
          <PaymentTabs 
            activeTab={selectedTab} 
            onTabChange={handleTabChange}
            canViewIncoming={canViewIncomingPayments}
            // canViewOutgoing={canViewOutgoingPayments}
          />
        </div>
        
        </div>

      {/* Content based on selected tab */}
      {selectedTab === "incoming" ? (
        <div className="px-3 sm:px-4 lg:px-[20px]">
          <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 lg:gap-6 min-w-0 w-full">
          {/* Main content area - widgets and table */}
          <div className={`flex flex-col min-w-0 ${isFilterOpen ? 'lg:flex-1 lg:max-w-[calc(100%-280px)]' : 'w-full'}`}>
            {/* Payment Widgets - Only show if user has permission to view incoming payments */}
            {/* {canViewIncomingPayments && (
              <div className="">
                <div className="flex items-end justify-end">
                  <div className="inline-flex items-center text-[#adb1c2] text-[12px] font-[400]">
                    for {getLastMonthRange()}
                  </div>
                </div>
                <PaymentWidgets />
              </div>
            )} */}
            
            {/* Main table content - Only show if user has permission */}
            {canViewIncomingPayments ? (
              <div className="flex-1 min-w-0">
                <div className="bg-white rounded-lg shadow-sm h-[calc(100vh-320px)] flex flex-col">
                  <PaymentHistoryTable<PaymentTransaction>
                    enableSorting={true}
                    data={displayData}
                    columnsConfig={paymentTableColumns}
                    dropdownOptions={dropdownOptions}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                    isSelectAllActive={isSelectAllActive}
                    setIsSelectAllActive={setIsSelectAllActive}
                    openModal={() => {}}
                    paginationConfig={{
                      initialPage: 1,
                      initialPerPage: 25,
                      perPageOptions: [10, 25, 50, 100],
                    }}
                    onPageChange={handlePageChange}
                    onPerPageChange={handlePerPageChange}
                    totalCount={totalCount}
                    currentPage={pagination.pageNumber}
                    currentPerPage={pagination.pageSize}
                    sortBy={sortBy}
                    onSortChange={handleSortChange}
                    className="flex-1 min-w-full"
                    enableRowSelection={true}
                    emptyStateText="No payments found!"
                    emptyStateIcon="/icons/empty-list-file.svg"
                    isLoading={isLoading || isRefreshing}
                    error={error}
                    onRetry={refreshData}
                  />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 sm:h-64 px-4">
                <div className="text-center">
                  <LightText2 text="You don't have permission to view the payment table." className="text-base sm:text-lg text-gray-500" />
                </div>
              </div>
            )}
          </div>
          
          {/* Filter panel - Only show if user has permission to view table */}
          {isFilterOpen && canViewIncomingPayments && (
            <>
              {/* Mobile: Full-screen overlay */}
              <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" onClick={handleCloseFilter}>
                <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h3 className="text-[16px] font-semibold text-gray-700">Filter</h3>
                    <div className="flex items-center gap-2">
                      <div 
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!hasEmptyFilter) {
                            handleAddFilter();
                          }
                        }}
                        className={`text-[13px] font-[400] ${
                          hasEmptyFilter 
                            ? 'text-gray-400 cursor-not-allowed' 
                            : 'text-[#a855f7] cursor-pointer hover:text-[#5a2d8a]'
                        }`}
                      >
                        Add Filter
                      </div>
                      <button
                        onClick={handleCloseFilter}
                        className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <X className="w-5 h-5 text-gray-500" />
                      </button>
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
                    <PaymentFilter 
                      ref={paymentFilterRef}
                      onClose={handleCloseFilter} 
                      onApplyFilters={handleApplyFilters}
                      appliedFilters={paymentStateFilters}
                      onResetFilters={handleResetFilters}
                      onAddFilter={handleAddFilter}
                    />
                  </div>
                </div>
              </div>

              {/* Desktop: Side panel */}
              <div className="hidden lg:block w-72 xl:w-80 2xl:w-96 min-w-[300px] max-w-[330px] flex-shrink-0">
                <div className="bg-white border border-gray-200 rounded-lg shadow-lg h-[calc(100vh-190px)] flex flex-col">
                  <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
                    <h3 className="text-[16px] font-semibold text-gray-700">Filter</h3>
                    <div className="flex items-center gap-2">
                      <div 
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!hasEmptyFilter) {
                            handleAddFilter();
                          }
                        }}
                        className={`text-[13px] font-[400] ${
                          hasEmptyFilter 
                            ? 'text-gray-400 cursor-not-allowed' 
                            : 'text-[#a855f7] cursor-pointer hover:text-[#5a2d8a]'
                        }`}
                      >
                        Add filter
                      </div>
                      <button
                        onClick={handleCloseFilter}
                        className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <X className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    <PaymentFilter 
                      ref={paymentFilterRef}
                      onClose={handleCloseFilter} 
                      onApplyFilters={handleApplyFilters}
                      appliedFilters={paymentStateFilters}
                      onResetFilters={handleResetFilters}
                      onAddFilter={handleAddFilter}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          </div>
        </div>
      // ) : selectedTab === "outgoing" && canViewOutgoingPayments ? (
      //   <div className="flex items-center justify-center h-48 sm:h-64 px-4">
      //     <div className="text-center">
      //       {/* <LightText2 text="Outgoing payments" className="text-xl sm:text-2xl font-semibold text-gray-700 mb-2" /> */}
      //       <LightText2 text="Coming Soon..." className="text-base sm:text-lg text-gray-500" />
      //     </div>
      //   </div>
      ) : (
        <div className="flex items-center justify-center h-48 sm:h-64 px-4">
          <div className="text-center">
            <LightText2 text="You don't have permission to view this content." className="text-base sm:text-lg text-gray-500" />
          </div>
        </div>
      )}

      {/* View Transaction Modal */}
      <ViewTransactionModal
        transactionId={selectedTransaction?.transaction_id || null}
        isOpen={isTransactionModalOpen}
        onClose={handleCloseTransactionModal}
        onDownloadReceipt={handleDownloadReceipt}
        isDownloading={isDownloading}
      />

      {/* Export Confirmation Modal */}
      {exportModalConfig && (
        <ExportConfirmationModal
          isOpen={isExportModalOpen}
          onClose={handleCloseExportModal}
          exportType={exportModalConfig.exportType}
          selectedCount={exportModalConfig.selectedCount}
          totalCount={totalCount || 0}
          isSelectAllActive={isSelectAllActive}
          transactionIds={exportModalConfig.transactionIds}
          onSuccess={handleExportSuccess}
        />
      )}
      </div>
  );
};

export default PaymentHistoryPage;
